import {
  LineChart as RechartsLineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  ReferenceArea,
} from 'recharts';
import { format } from 'date-fns';
import { clearDepthCm } from '@/lib/clearToBottom';
import { toNumberOrNull } from '@/lib/number';

interface DataPoint {
  date: string;
  slot: string;
  /** 'check' = an ad-hoc pre-stocking water check rather than a slot reading. */
  kind?: string;
  [key: string]: string | number | boolean | null | undefined;
}

interface Thresholds {
  warning?: number | [number, number];
  critical?: number | [number, number];
}

interface LineChartProps {
  data: DataPoint[];
  dataKey: string;
  label: string;
  unit: string;
  thresholds?: Thresholds;
  height?: number;
  /** A clear-to-bottom row (#396) has no value of its own: it is plotted at that row's own depth (metres, x 100). */
  clearToBottom?: { flagKey: string; depthKey: string };
}

export function LineChart({
  data,
  dataKey,
  label,
  unit,
  thresholds,
  height = 300,
  clearToBottom,
}: LineChartProps) {
  const isClear = (d: DataPoint) => clearToBottom !== undefined && d[clearToBottom.flagKey] === true;
  const clearPlotted = (d: DataPoint) => (clearToBottom ? clearDepthCm(d[clearToBottom.depthKey] as string | number | null) : null);
  const undrawnClear = data.filter((d) => isClear(d) && clearPlotted(d) === null).length;

  // Format data for chart (API returns Decimal as strings, parse to numbers)
  const chartData = data
    .map((d) => {
      const raw = isClear(d) ? clearPlotted(d) : d[dataKey];
      if (raw === null || raw === undefined) return null;
      const value = typeof raw === 'string' ? parseFloat(raw) : (raw as number);
      return {
        ...d,
        timestamp: `${d.date} ${d.slot}`,
        value: isNaN(value) ? null : value,
        clear: isClear(d),
      };
    })
    .filter((d) => d !== null && d.value !== null) as Array<DataPoint & { timestamp: string; value: number; clear: boolean }>;

  const note = undrawnClear > 0 && (
    <p className="mt-1 text-xs text-gray-500">
      {undrawnClear} clear {undrawnClear === 1 ? 'reading is' : 'readings are'} not drawn: no pond depth recorded
    </p>
  );

  if (chartData.length === 0) {
    return (
      <>
        <div
          className="flex items-center justify-center text-gray-400"
          style={{ height }}
        >
          No data available
        </div>
        {note}
      </>
    );
  }

  // A day with no 7am point (a series that only has 3pm readings) would otherwise label every tick with the bare slot.
  const mornings = new Set(chartData.filter((d) => d.slot === '7am').map((d) => d.date));
  const firstWithoutMorning = new Set<string>();
  const seenDates = new Set<string>();
  for (const d of chartData) {
    if (!seenDates.has(d.date) && !mornings.has(d.date)) firstWithoutMorning.add(d.timestamp);
    seenDates.add(d.date);
  }

  const values = chartData.map((d) => d.value);
  const minValue = Math.min(...values);
  const maxValue = Math.max(...values);

  return (
    <>
      <ResponsiveContainer width="100%" height={height}>
        <RechartsLineChart
          data={chartData}
          margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
          <XAxis
            dataKey="timestamp"
            tick={{ fontSize: 12, fill: '#6b7280' }}
            tickFormatter={(v) => {
              const parts = v.split(' ');
              // A water check's "slot" is its clock time, so it carries its own date.
              if (/^\d{2}:\d{2}$/.test(parts[1])) return `${format(new Date(parts[0]), 'MMM d')} ${parts[1]}`;
              if (parts[1] === '7am' || firstWithoutMorning.has(v)) {
                const date = new Date(parts[0]);
                const currentYear = new Date().getFullYear();
                // Include year if not current year
                return date.getFullYear() !== currentYear
                  ? format(date, 'MMM d, yy')
                  : format(date, 'MMM d');
              }
              return parts[1];
            }}
          />
          <YAxis
            domain={['auto', 'auto']}
            tick={{ fontSize: 12, fill: '#6b7280' }}
            tickFormatter={(v) => {
              const num = Number(v);
              const decimals = Math.abs(num) < 1 ? 2 : 1;
              return `${num.toFixed(decimals)}${unit}`;
            }}
          />
          <Tooltip
            content={({ active, payload }) => {
              if (!active || !payload?.length) return null;
              const point = payload[0].payload;
              return (
                <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3">
                  <p className="text-sm text-gray-500">
                    {format(new Date(point.date), 'MMM d, yyyy')} {point.slot}
                    {point.kind === 'check' && ' · water check'}
                  </p>
                  <p className="font-semibold text-gray-900">
                    {point.clear ? 'Clear to bottom' : label}:{' '}
                    {point.value !== null ? `${point.value}${unit}` : 'N/A'}
                  </p>
                  {point.clear && clearToBottom && (
                    <p className="text-xs text-gray-500">
                      Pond depth in this reading: {toNumberOrNull(point[clearToBottom.depthKey])?.toFixed(2)} m
                    </p>
                  )}
                </div>
              );
            }}
          />

          {/* Threshold reference lines/areas */}
          {thresholds && renderThresholds(thresholds, minValue, maxValue)}

          {/* Data line */}
          <Line
            type="monotone"
            dataKey="value"
            stroke="#3b82f6"
            strokeWidth={2}
            dot={({ cx, cy, payload, index }) =>
              payload.kind === 'check' ? (
                <circle key={index} cx={cx} cy={cy} r={4} fill="#ffffff" stroke="#d97706" strokeWidth={2} />
              ) : payload.clear ? (
                <g key={index}>
                  <circle cx={cx} cy={cy} r={3} fill="#3b82f6" stroke="#3b82f6" strokeWidth={2} />
                  <text x={cx} y={(cy ?? 0) - 9} textAnchor="middle" fontSize={11} fontWeight={600} fill="#111827">
                    {payload.value}
                  </text>
                </g>
              ) : (
                <circle key={index} cx={cx} cy={cy} r={3} fill="#3b82f6" stroke="#3b82f6" strokeWidth={2} />
              )
            }
            activeDot={{ r: 5 }}
            connectNulls
          />
        </RechartsLineChart>
      </ResponsiveContainer>
      {chartData.some((d) => d.clear) && (
        <div className="mt-2 flex flex-wrap items-center justify-center gap-x-5 gap-y-1 text-xs text-gray-500">
          <span className="flex items-center gap-1.5">
            <span className="h-2 w-2 rounded-full bg-blue-500" />
            measured
          </span>
          <span className="flex items-center gap-1.5">
            <span className="h-2 w-2 rounded-full bg-blue-500" />
            clear to bottom (plotted at the pond depth)
          </span>
        </div>
      )}
      {note}
    </>
  );
}

function renderThresholds(
  thresholds: Thresholds,
  minValue: number,
  maxValue: number
): React.ReactNode[] {
  const elements: React.ReactNode[] = [];
  const yMin = minValue - 10;
  const yMax = maxValue + 10;

  // Warning threshold (orange)
  if (thresholds.warning) {
    if (Array.isArray(thresholds.warning)) {
      // Range threshold - show area outside range as warning
      elements.push(
        <ReferenceArea
          key="warning-low"
          y1={yMin}
          y2={thresholds.warning[0]}
          fill="#fef3c7"
          fillOpacity={0.5}
        />,
        <ReferenceArea
          key="warning-high"
          y1={thresholds.warning[1]}
          y2={yMax}
          fill="#fef3c7"
          fillOpacity={0.5}
        />,
        <ReferenceLine
          key="warning-line-low"
          y={thresholds.warning[0]}
          stroke="#f59e0b"
          strokeDasharray="5 5"
        />,
        <ReferenceLine
          key="warning-line-high"
          y={thresholds.warning[1]}
          stroke="#f59e0b"
          strokeDasharray="5 5"
        />
      );
    } else {
      // Single threshold (below is warning)
      elements.push(
        <ReferenceArea
          key="warning-area"
          y1={yMin}
          y2={thresholds.warning}
          fill="#fef3c7"
          fillOpacity={0.3}
        />,
        <ReferenceLine
          key="warning-line"
          y={thresholds.warning}
          stroke="#f59e0b"
          strokeDasharray="5 5"
          label={{
            value: 'Warning',
            position: 'right',
            fill: '#f59e0b',
            fontSize: 10,
          }}
        />
      );
    }
  }

  return elements;
}
